import type { JSONSchemaNode } from '@/lib/api/types';
import {
  APPLICATION_REGIONS,
  CREDENTIAL_GROUPS,
  CREDENTIAL_PLACEMENTS,
  CREDENTIAL_STATUSES,
  FACT_STATES,
  LOCATION_KINDS,
  PRODUCT_FAMILIES,
  PROJECT_FACT_KEYS,
  REVIEWER_STATES,
  TEAM_GROUPS,
  enumLabels,
  type VocabOption,
} from './vocab';

/**
 * Content-collection registry (plan §3.3 / spec §9.3). One config-driven engine serves all
 * nine collections; adding a collection is a single entry here.
 *
 * The backend serves the authoritative doc schema for every collection at
 * `GET /admin/collection-schemas` (be-tepco internal/docschema/schemas.go), keyed by
 * `entity`. `docSchema` here is only the **offline fallback**, a faithful mirror of that
 * schema used when the endpoint is unreachable — keep the two in sync. Field labels are not
 * titled here: both the served and the fallback schema resolve a property's label from
 * `docFields.<property>` (see resolveSchema.ts), so one i18n entry labels the field in EN and
 * AR whichever schema rendered it. Facet keys are the backend facet names (the list query
 * parameter), with options from the canonical vocabularies in `vocab.ts`.
 */
export interface FacetDef {
  key: string;
  label: string;
  /** `ref-slug` resolves options from another collection (industries / product-families). */
  type: 'text' | 'boolean' | 'enum' | 'ref-slug';
  /** Static options for `enum` facets. */
  options?: VocabOption[];
  /** Collection key for `ref-slug` facets (e.g. `industries`, `product-families`). */
  refCollection?: string;
}

export interface CollectionConfig {
  /** route segment under /admin and the API path segment. */
  key: string;
  /**
   * Backend entity name: the key of this collection's schema in `GET /admin/collection-schemas`,
   * the revisions `entity_type` and the search hit `entity_type`.
   */
  entity: string;
  /** i18n nav key. */
  navKey: string;
  /** Display label (EN fallback for chrome). */
  label: string;
  /** Singular i18n label key — used for the "New {singular}" editor heading. */
  singularLabel: string;
  identType: 'slug' | 'key';
  hasSeo: boolean;
  /** When true, items can only be edited — no create or delete (a fixed register of records). */
  editOnly?: boolean;
  /**
   * Records are ordered by hand, so the list offers a Reorder mode. A static fallback for the
   * served capabilities' `orderable`, which wins once loaded.
   */
  orderable?: boolean;
  /**
   * Records are also ordered per page they are placed on (credentials): the list param that
   * selects a placement and the places. Capabilities' `placements` wins once loaded.
   */
  placements?: { param: string; options: VocabOption[] };
  facets: FacetDef[];
  /** JSON-Schema for the bilingual `doc` form (offline fallback for the served schema). */
  docSchema: JSONSchemaNode;
}

const I18N: JSONSchemaNode = { $ref: '#/$defs/I18n' };
const I18N_RICH: JSONSchemaNode = { $ref: '#/$defs/I18nRich' };

const DEFS = {
  I18n: { type: 'object', properties: { en: { type: 'string' }, ar: { type: 'string' } } },
  I18nRich: { type: 'object', properties: { en: { type: 'string' }, ar: { type: 'string' } } },
} as const;

export const SCHEMA_DEFS = DEFS as unknown as Record<string, JSONSchemaNode>;

// ── Reusable doc-schema building blocks (mirror the backend helpers) ─────────
const str = (hint?: string): JSONSchemaNode => ({
  type: 'string',
  ...(hint ? { 'x-hint': hint } : {}),
});
const int = (): JSONSchemaNode => ({ type: 'integer' });
const bool = (hint?: string): JSONSchemaNode => ({
  type: 'boolean',
  ...(hint ? { 'x-hint': hint } : {}),
});
const list = (items: JSONSchemaNode, hint?: string): JSONSchemaNode => ({
  type: 'array',
  items,
  ...(hint ? { 'x-hint': hint } : {}),
});
/** A single media-asset reference (uuid). */
const media = (hint?: string): JSONSchemaNode => ({
  type: 'string',
  format: 'uuid',
  ...(hint ? { 'x-hint': hint } : {}),
});
const withHint = (node: JSONSchemaNode, hint: string): JSONSchemaNode => ({
  ...node,
  'x-hint': hint,
});
const isoDate = (): JSONSchemaNode => ({ type: 'string', pattern: '^\\d{4}-\\d{2}-\\d{2}$' });
/**
 * An OPEN filter token (article category, project sector): the website derives its filter
 * chips from the values present and labels them from page copy, so a new value needs a copy
 * label, not a code change. Mirrors the backend `feedToken`.
 */
const feedToken = (hint: string): JSONSchemaNode => ({
  type: 'string',
  pattern: '^[a-z][a-z0-9-]{0,31}$',
  'x-hint': hint,
});

const enumField = (options: VocabOption[], hint?: string): JSONSchemaNode => ({
  type: 'string',
  enum: options.map((o) => o.value),
  'x-enum-labels': enumLabels(options),
  ...(hint ? { 'x-hint': hint } : {}),
});
/** A list of distinct closed-choice values (rendered as a checkbox set). */
const enumList = (options: VocabOption[], hint?: string): JSONSchemaNode => ({
  type: 'array',
  uniqueItems: true,
  items: enumField(options),
  ...(hint ? { 'x-hint': hint } : {}),
});

const docSchema = (
  properties: Record<string, JSONSchemaNode>,
  required?: string[],
): JSONSchemaNode => ({
  type: 'object',
  $defs: SCHEMA_DEFS,
  properties,
  ...(required ? { required } : {}),
});

export const COLLECTIONS: CollectionConfig[] = [
  {
    key: 'industries',
    entity: 'industry',
    navKey: 'industries',
    label: 'collections.industries.label',
    singularLabel: 'collections.industries.singular',
    identType: 'slug',
    hasSeo: true,
    orderable: true,
    facets: [],
    docSchema: docSchema({
      kicker: I18N,
      summary: I18N,
      chips: list(str(), 'Short Latin tokens (standards, ratings), shown untranslated.'),
      rows: list(I18N),
      image_id: media(),
      accent: str('CSS colour for the card accent.'),
      finder_only: bool('When on, the sector is a home-finder option only, with no card.'),
    }),
  },
  {
    key: 'projects',
    entity: 'project',
    navKey: 'projects',
    label: 'collections.projects.label',
    singularLabel: 'collections.projects.singular',
    identType: 'slug',
    hasSeo: true,
    orderable: true,
    facets: [{ key: 'sector', label: 'docFields.sector', type: 'text' }],
    docSchema: docSchema({
      sector: feedToken(
        'Filter token, lowercase (health, infra, edu, …). A new sector also needs its chip label in page copy pg_projects → feed.categories.',
      ),
      eyebrow: I18N,
      facts: list({
        type: 'object',
        required: ['key', 'state'],
        properties: {
          key: enumField(PROJECT_FACT_KEYS),
          label: I18N,
          text: I18N_RICH,
          state: enumField(
            FACT_STATES,
            'Provisional facts render marked as unconfirmed; withheld facts render as not disclosed.',
          ),
        },
      }),
      brief: I18N_RICH,
      checks: list(I18N),
      image_id: media(),
      caption: I18N,
    }),
  },
  {
    key: 'articles',
    entity: 'article',
    navKey: 'articles',
    label: 'collections.articles.label',
    singularLabel: 'collections.articles.singular',
    identType: 'slug',
    hasSeo: true,
    orderable: true,
    facets: [{ key: 'category', label: 'docFields.category', type: 'text' }],
    docSchema: docSchema({
      category: feedToken(
        'Filter token, lowercase (grid, local, proc, qa, …). A new category also needs its chip label in page copy pg_insights → feed.categories.',
      ),
      date: isoDate(),
      read_minutes: int(),
      byline: I18N,
      reviewer: withHint(
        I18N_RICH,
        'May carry <span class="prov"> while the reviewer is unconfirmed.',
      ),
      reviewer_state: enumField(REVIEWER_STATES),
      summary: I18N,
      card_image_id: media(),
      hero_image_id: media(),
      lead: I18N_RICH,
      body: withHint(
        I18N_RICH,
        'Blocks: p (.article__lead, .note), h2, h3, ul, ol, li, figure.tbl > table, figcaption.',
      ),
      foot: I18N_RICH,
      related_ctas: list({ type: 'object', properties: { label: I18N, href: str() } }),
    }),
  },
  {
    key: 'job-postings',
    entity: 'job',
    navKey: 'jobPostings',
    label: 'collections.jobPostings.label',
    singularLabel: 'collections.jobPostings.singular',
    identType: 'slug',
    hasSeo: true,
    orderable: true,
    facets: [
      { key: 'region', label: 'docFields.regions', type: 'enum', options: APPLICATION_REGIONS },
    ],
    docSchema: docSchema({
      code: {
        type: 'string',
        pattern: '^[A-Za-z0-9][A-Za-z0-9-]{0,31}$',
        'x-hint':
          'The role code applicants pick on the careers form (e.g. SSE). Unique among live postings; SPEC is reserved for open applications.',
      },
      location_line: I18N,
      posted: isoDate(),
      seats: { type: 'integer', minimum: 1 },
      summary: I18N,
      bullets: list(I18N),
      regions: enumList(APPLICATION_REGIONS),
    }),
  },
  {
    key: 'products',
    entity: 'product',
    navKey: 'products',
    label: 'collections.products.label',
    singularLabel: 'collections.products.singular',
    identType: 'slug',
    hasSeo: true,
    orderable: true,
    facets: [{ key: 'family', label: 'docFields.family', type: 'enum', options: PRODUCT_FAMILIES }],
    // `name` is the editor's title field (mirrored into doc.name by the service). `family` is
    // the category KEY; one picker writes both it and `family_id` so the two never drift.
    docSchema: docSchema(
      {
        family: {
          type: 'string',
          minLength: 1,
          'x-ref-key': 'product-families',
          'x-ref-id-field': 'family_id',
          'x-hint': 'The category key: lv, mv or smart-grid.',
        },
        kicker: I18N,
        summary: I18N,
        chips: list(str(), 'Short Latin tokens (ratings, standards), shown untranslated.'),
        rows: list(I18N),
        card_drawing_id: media('SVG drawing on the /products card.'),
        drawing_id: media(
          "SVG drawing on the category page. If a rating changes, the drawing's title block must change with it.",
        ),
        photo_id: media('Optional photograph. When set it replaces the drawing.'),
        drawing_alt: I18N,
        drawing_caption: I18N,
        spec: {
          type: 'object',
          properties: {
            eyebrow: I18N,
            title: I18N,
            columns: list(I18N),
            rows: list({ type: 'object', properties: { param: I18N, value: I18N } }),
            caption: I18N,
          },
        },
        licence_note: I18N_RICH,
        accent: str('CSS colour for the card accent.'),
        anchor: str('In-page anchor id on the category page, e.g. prismaset.'),
      },
      ['family', 'name'],
    ),
  },
  {
    key: 'locations',
    entity: 'location',
    navKey: 'locations',
    label: 'collections.locations.label',
    singularLabel: 'collections.locations.singular',
    identType: 'key',
    hasSeo: false,
    orderable: true,
    facets: [{ key: 'kind', label: 'docFields.kind', type: 'enum', options: LOCATION_KINDS }],
    docSchema: docSchema({
      city: I18N,
      region: I18N,
      kind: enumField(LOCATION_KINDS),
      blurb: I18N,
      phone_short: str('Digits only, e.g. 920018640.'),
      mobile: str('International format, e.g. +966565993839.'),
      email: str(),
      address: I18N,
    }),
  },
  {
    // A group company (about page "Six specialised companies"). `name` is the title field.
    key: 'subsidiaries',
    entity: 'subsidiary',
    navKey: 'subsidiaries',
    label: 'collections.subsidiaries.label',
    singularLabel: 'collections.subsidiaries.singular',
    identType: 'key',
    hasSeo: false,
    orderable: true,
    facets: [],
    docSchema: docSchema({ kicker: I18N, summary: I18N, image_id: media() }),
  },
  {
    // Board members and executives. `name` is the title field; `group` splits the register
    // by render surface.
    key: 'team-members',
    entity: 'team_member',
    navKey: 'teamMembers',
    label: 'collections.teamMembers.label',
    singularLabel: 'collections.teamMembers.singular',
    identType: 'key',
    hasSeo: false,
    orderable: true,
    facets: [{ key: 'group', label: 'docFields.group', type: 'enum', options: TEAM_GROUPS }],
    docSchema: docSchema({
      role: I18N,
      group: enumField(TEAM_GROUPS),
      line: I18N,
      portrait_id: media(),
    }),
  },
  {
    // One registration / licence / standard / permit / programme / capital / evidence plate
    // (plan D4). `label` is the title field. A plate renders on every page block listed in
    // `placements`, ordered by `placement_order` within each block.
    key: 'credentials',
    entity: 'credential',
    navKey: 'credentials',
    label: 'collections.credentials.label',
    singularLabel: 'collections.credentials.singular',
    identType: 'key',
    hasSeo: false,
    orderable: true,
    placements: { param: 'placement', options: CREDENTIAL_PLACEMENTS },
    facets: [
      { key: 'group', label: 'docFields.group', type: 'enum', options: CREDENTIAL_GROUPS },
      {
        key: 'placement',
        label: 'docFields.placements',
        type: 'enum',
        options: CREDENTIAL_PLACEMENTS,
      },
    ],
    docSchema: docSchema(
      {
        group: enumField(CREDENTIAL_GROUPS),
        value: str(
          'Shown exactly as typed, in a monospaced face (e.g. 1010123456). Not translated.',
        ),
        authority: I18N,
        status: enumField(
          CREDENTIAL_STATUSES,
          'This is a legal claim — confirm it against the issued document before publishing.',
        ),
        status_label_override: withHint(
          I18N,
          'Optional. Replaces the standard wording for this status on this plate only.',
        ),
        note: I18N_RICH,
        placements: enumList(CREDENTIAL_PLACEMENTS, 'Every page block this plate appears on.'),
        placement_order: {
          type: ['object', 'null'],
          additionalProperties: int(),
          propertyNames: enumField(CREDENTIAL_PLACEMENTS),
          'x-hint':
            'Position per placement (lower first). A placement without a number sorts last.',
        },
      },
      ['label', 'group', 'status', 'placements'],
    ),
  },
];

export function getCollection(key: string): CollectionConfig | undefined {
  return COLLECTIONS.find((c) => c.key === key);
}

/** Collection route key → backend entity name (schema key, revisions + search entity_type). */
export const ENTITY_TYPE: Record<string, string> = Object.fromEntries(
  COLLECTIONS.map((c) => [c.key, c.entity]),
);

/** Backend entity name → collection route key (search hits deep-link through this). */
export const ENTITY_TYPE_TO_COLLECTION: Record<string, string> = Object.fromEntries(
  COLLECTIONS.map((c) => [c.entity, c.key]),
);
